// In-memory search for the Hospitals & ORs table: one query narrows hospitals and their rooms together.
//
// A hospital matches on its name or any part of its address, and then shows ALL of its rooms. Failing
// that, it still shows if one of its rooms matches by name or required credential — but only with the
// matching rooms, and flagged `matchedByRoom` so the table opens that row to show why it is there.

export interface SearchableHospital {
  hospitalId: string;
  name: string;
  address?: { street?: string; city?: string; state?: string; zipCode?: string; country?: string };
}

export interface SearchableRoom {
  operatingRoomId: string;
  hospitalId: string;
  name: string;
  credential?: string;
}

export interface HospitalSearchResult<H extends SearchableHospital, R extends SearchableRoom> {
  hospital: H;
  rooms: R[];
  matchedByRoom: boolean;
}

export const formatAddress = (address?: SearchableHospital['address']): string => {
  if (!address) return '';
  const { street, city, state, zipCode, country } = address;
  const cityLine = [city, [state, zipCode].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  return [street, cityLine, country].filter(Boolean).join(', ');
};

const byRoomName = (a: SearchableRoom, b: SearchableRoom) =>
  a.name.localeCompare(b.name, undefined, { numeric: true });

export const searchHospitals = <H extends SearchableHospital, R extends SearchableRoom>(
  hospitals: H[],
  rooms: R[],
  query: string,
): HospitalSearchResult<H, R>[] => {
  const q = query.trim().toLowerCase();
  const roomsByHospital = new Map<string, R[]>();
  for (const room of rooms) {
    const list = roomsByHospital.get(room.hospitalId) ?? [];
    list.push(room);
    roomsByHospital.set(room.hospitalId, list);
  }
  const results: HospitalSearchResult<H, R>[] = [];
  for (const hospital of hospitals) {
    const own = [...(roomsByHospital.get(hospital.hospitalId) ?? [])].sort(byRoomName);
    const hospitalText = `${hospital.name} ${formatAddress(hospital.address)}`.toLowerCase();
    if (!q || hospitalText.includes(q)) {
      results.push({ hospital, rooms: own, matchedByRoom: false });
      continue;
    }
    const matching = own.filter(r => `${r.name} ${r.credential ?? ''}`.toLowerCase().includes(q));
    if (matching.length > 0) results.push({ hospital, rooms: matching, matchedByRoom: true });
  }
  return results;
};
